import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import {
  ArrowUp,
  Bot,
  Boxes,
  ChevronDown,
  CircleStop,
  Cpu,
  FileText,
  Menu,
  MessageSquarePlus,
  Network,
  Paperclip,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import { getModel, MODELS, MODE_PROFILES } from "./models";
import type {
  AccountProfile,
  AppSettings,
  ChatMessage,
  FileAttachment,
  GpuCapability,
  ModelDefinition,
  ModelLoadState,
  SwarmSession,
  InferenceMode,
} from "./types";
import { createId, formatBytes } from "./lib/ids";
import { readAttachment } from "./lib/files";
import { buildInferenceMessages } from "./lib/prompt";
import { planNexusInference } from "./lib/nexus-patent-routing";
import { InferenceClient } from "./lib/inference-client";
import {
  SwarmClient,
  type ConnectedPeer,
  type DistributedInferenceResult,
  type RemoteInferenceCancel,
  type RemoteInferenceTask,
  type SwarmProgress,
  type SwarmGenerationPayload,
} from "./lib/swarm-client";
import {
  loadDeviceName,
  loadModelId,
  loadSettings,
  saveDeviceName,
  saveModelId,
  saveSettings,
} from "./lib/storage";
import { clearMessagesFromDb, loadMessagesFromDb, persistMessages } from "./lib/indexed-db";
import { MessageBubble } from "./components/MessageBubble";
import { ModelLibrary } from "./components/ModelLibrary";
import { SettingsDrawer } from "./components/SettingsDrawer";
import { BootSplash } from "./components/BootSplash";
import { AuthGate } from "./components/AuthGate";

const EMPTY_GPU: GpuCapability = {
  available: false,
  adapterName: "利用不可",
  architecture: "unknown",
  shaderF16: false,
  error: "AIを確認できませんでした。",
};

const ROLE_INSTRUCTIONS: Record<string, string> = {
  primary: "主回答者として、質問へ直接・正確に答えてください。",
  critic: "批評担当として、見落としや誤りを避けた別の回答案を作ってください。",
  verifier: "検証担当として、事実関係と論理を重視した回答案を作ってください。",
  alternative: "別視点担当として、主回答と重複しすぎない有用な回答案を作ってください。",
};

function appendRoleInstruction(payload: SwarmGenerationPayload, role: string): SwarmGenerationPayload {
  const instruction = ROLE_INSTRUCTIONS[role] ?? ROLE_INSTRUCTIONS.alternative;
  const messages = payload.messages.map((message) => ({ ...message }));
  const systemIndex = messages.findIndex((message) => message.role === "system");
  if (systemIndex >= 0) {
    messages[systemIndex].content = `${messages[systemIndex].content}\n\n回答補助:\n${instruction}`;
    return { ...payload, messages };
  }
  const firstUser = messages.findIndex((message) => message.role === "user");
  if (firstUser >= 0) {
    messages[firstUser].content = `回答補助:\n${instruction}\n\n${messages[firstUser].content}`;
  }
  return {
    ...payload,
    messages,
  };
}

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function truncateUtf8(value: string, maxBytes: number): string {
  const encoder = new TextEncoder();
  if (encoder.encode(value).byteLength <= maxBytes) return value;

  let low = 0;
  let high = value.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (encoder.encode(value.slice(0, middle)).byteLength <= maxBytes) low = middle;
    else high = middle - 1;
  }
  if (low > 0 && /[\uD800-\uDBFF]/u.test(value[low - 1])) low -= 1;
  return value.slice(0, low);
}

function statusText(loadState: ModelLoadState, gpu: GpuCapability | null, model: ModelDefinition): string {
  if (!gpu) return "AIを準備中";
  if (!gpu.available) return "AIを利用できません";
  if (loadState.status === "loading") return `${loadState.message} ${Math.round(loadState.progress)}%`;
  if (loadState.status === "ready" && loadState.modelId === model.id) return `${model.name} · 準備完了`;
  if (loadState.status === "error") return "AIの準備に失敗";
  return "AIチャットを開始できます";
}

export default function App() {
  const inference = useMemo(() => new InferenceClient(), []);
  const swarmClient = useMemo(() => new SwarmClient(), []);
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());
  const [booting, setBooting] = useState(true);
  const [selectedModelId, setSelectedModelId] = useState(() => loadModelId());
  const selectedModel = useMemo(() => getModel(selectedModelId), [selectedModelId]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyReady, setHistoryReady] = useState(false);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [gpu, setGpu] = useState<GpuCapability | null>(null);
  const [loadState, setLoadState] = useState<ModelLoadState>({ status: "idle" });
  const [loadedModelId, setLoadedModelId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modelsOpen, setModelsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [swarm, setSwarm] = useState<SwarmSession | null>(null);
  const [peers, setPeers] = useState<ConnectedPeer[]>([]);
  const [signalingRtt, setSignalingRtt] = useState<number | null>(null);
  const [turnConfigured, setTurnConfigured] = useState(false);
  const [inferenceMode, setInferenceMode] = useState<InferenceMode>("local");
  const [contributing, setContributing] = useState(true);
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [accountChecked, setAccountChecked] = useState(false);
  const [deviceName, setDeviceName] = useState(() => loadDeviceName());
  const [remoteActivity, setRemoteActivity] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedModelRef = useRef(selectedModel);
  const loadedModelIdRef = useRef<string | null>(null);
  const loadPromiseRef = useRef<{ modelId: string; promise: Promise<void> } | null>(null);
  const activeGenerationAbortRef = useRef<AbortController | null>(null);
  const remoteTaskAbortsRef = useRef(new Map<string, AbortController>());
  const contributingRef = useRef(contributing);

  useEffect(() => {
    selectedModelRef.current = selectedModel;
  }, [selectedModel]);

  useEffect(() => {
    loadedModelIdRef.current = loadedModelId;
  }, [loadedModelId]);

  useEffect(() => {
    contributingRef.current = contributing;
    if (swarm && gpu) {
      const readyAndIdle = inferenceMode !== "local" && contributing && !generating && loadedModelId === selectedModel.id;
      const scope = inferenceMode === "world" ? "world" : "friend";
      swarmClient.updatePresence(selectedModel.id, gpu, readyAndIdle, scope);
    }
  }, [contributing, generating, gpu, inferenceMode, loadedModelId, selectedModel.id, swarm, swarmClient]);

  useEffect(() => {
    let active = true;
    void loadMessagesFromDb()
      .then((stored) => active && setMessages(stored))
      .catch(() => undefined)
      .finally(() => active && setHistoryReady(true));
    void inference.checkGpu().then((capability) => active && setGpu(capability)).catch(() => active && setGpu(EMPTY_GPU));
    void fetchAccount()
      .then((profile) => {
        if (!active) return;
        setAccount(profile);
        if (profile && !deviceName.trim()) setDeviceName(profile.displayName.slice(0, 32));
      })
      .catch(() => undefined)
      .finally(() => active && setAccountChecked(true));
    return () => {
      active = false;
      inference.destroy();
      void swarmClient.leave();
    };
  }, [inference, swarmClient]);

  useEffect(() => {
    if (!historyReady) return;
    const timer = window.setTimeout(() => void persistMessages(messages), 250);
    return () => window.clearTimeout(timer);
  }, [historyReady, messages]);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  useEffect(() => {
    saveDeviceName(deviceName);
  }, [deviceName]);

  useEffect(() => {
    if (!generating) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [generating, messages]);

  const ensureLoaded = useCallback(
    async (model: ModelDefinition = selectedModelRef.current) => {
      if (loadedModelIdRef.current === model.id) return;
      while (loadPromiseRef.current) {
        const activeLoad = loadPromiseRef.current;
        try {
          await activeLoad.promise;
        } catch {
          // The requested model is checked again below and can start a clean load.
        }
        if (loadedModelIdRef.current === model.id) return;
      }
      setLoadedModelId(null);
      loadedModelIdRef.current = null;
      setLoadState({ status: "checking" });
      const promise = inference
        .load(model, setLoadState)
        .then(() => {
          setLoadedModelId(model.id);
          loadedModelIdRef.current = model.id;
          setLoadState({ status: "ready", modelId: model.id });
        })
        .catch((cause) => {
          setLoadedModelId(null);
          loadedModelIdRef.current = null;
          const message = errorText(cause);
          setLoadState({ status: "error", message });
          throw cause;
        })
        .finally(() => {
          if (loadPromiseRef.current?.promise === promise) loadPromiseRef.current = null;
        });
      loadPromiseRef.current = { modelId: model.id, promise };
      return promise;
    },
    [gpu, inference],
  );

  const updateAssistant = useCallback(
    (id: string, update: (message: ChatMessage) => ChatMessage) => {
      setMessages((current) => current.map((message) => (message.id === id ? update(message) : message)));
    },
    [],
  );

  const preparePayload = useCallback(
    async (payload: SwarmGenerationPayload, signal?: AbortSignal): Promise<SwarmGenerationPayload> => {
      const model = getModel(payload.modelId);
      await ensureLoaded(model);
      if (signal?.aborted) throw Object.assign(new Error("生成を停止しました。"), { name: "AbortError" });
      const prepared = await inference.prepare(payload.messages, model, {
        mode: payload.mode,
        maxNewTokens: payload.maxNewTokens,
        contextTokens: payload.contextTokens,
      });
      return { ...payload, messages: prepared.messages };
    },
    [ensureLoaded, inference],
  );

  const runGeneration = useCallback(
    async (
      payload: SwarmGenerationPayload,
      onToken: (output: string, tokensPerSecond: number) => void,
      signal?: AbortSignal,
    ) => {
      const model = getModel(payload.modelId);
      const prepared = await preparePayload(payload, signal);
      if (signal?.aborted) throw Object.assign(new Error("生成を停止しました。"), { name: "AbortError" });
      return inference.generate(prepared.messages, model, {
        mode: payload.mode,
        temperature: payload.temperature,
        maxNewTokens: payload.maxNewTokens,
        contextTokens: payload.contextTokens,
        ragEnabled: settings.ragEnabled,
        onToken,
        signal,
      });
    },
    [inference, preparePayload, settings.ragEnabled],
  );

  useEffect(() => {
    const onPeers = (event: Event) => {
      setPeers((event as CustomEvent<ConnectedPeer[]>).detail);
      setTurnConfigured(swarmClient.hasTurn);
    };
    const onLatency = (event: Event) => {
      const detail = (event as CustomEvent<{ signalingRttMs: number | null; peers: ConnectedPeer[] }>).detail;
      setSignalingRtt(detail.signalingRttMs);
      setPeers(detail.peers);
    };
    const onTask = (event: Event) => {
      const task = (event as CustomEvent<RemoteInferenceTask>).detail;
      if (inferenceMode === "local") {
        swarmClient.sendProgress(task.taskId, task.batchId, "cancelled");
        return;
      }
      if (!contributingRef.current) return;
      if (loadedModelIdRef.current !== task.payload.modelId) {
        swarmClient.sendProgress(task.taskId, task.batchId, "error");
        setError("この環境では現在のリクエストを処理できませんでした。");
        return;
      }
      const taskKey = `${task.fromPeerId}:${task.taskId}`;
      if (remoteTaskAbortsRef.current.size > 0 || remoteTaskAbortsRef.current.has(taskKey)) return;
      const controller = new AbortController();
      remoteTaskAbortsRef.current.set(taskKey, controller);
      void (async () => {
        try {
          swarmClient.sendProgress(task.taskId, task.batchId, "running");
          setRemoteActivity(`${ROLE_INSTRUCTIONS[task.role] ? task.role : "task"} を処理中`);
          const payload = appendRoleInstruction(task.payload, task.role);
          const result = await runGeneration(payload, () => undefined, controller.signal);
          if (!controller.signal.aborted) {
            await swarmClient.sendResult(task.fromPeerId, {
              taskId: task.taskId,
              batchId: task.batchId,
              role: task.role,
              text: result.text,
              tokens: result.tokens,
              tokensPerSecond: result.tokensPerSecond,
            });
            swarmClient.sendProgress(task.taskId, task.batchId, "complete");
          }
        } catch (cause) {
          if (cause instanceof Error && cause.name === "AbortError") {
            swarmClient.sendProgress(task.taskId, task.batchId, "cancelled");
          } else {
            swarmClient.sendProgress(task.taskId, task.batchId, "error");
            setError(`共有チャット: ${errorText(cause)}`);
          }
        } finally {
          remoteTaskAbortsRef.current.delete(taskKey);
          setRemoteActivity("");
          if (gpu) {
            const selected = selectedModelRef.current.id;
            const ready = contributingRef.current
              && activeGenerationAbortRef.current === null
              && loadedModelIdRef.current === selected;
            const scope = inferenceMode === "world" ? "world" : "friend";
            swarmClient.updatePresence(selected, gpu, ready, scope);
          }
        }
      })();
    };
    const onCancel = (event: Event) => {
      const cancellation = (event as CustomEvent<RemoteInferenceCancel>).detail;
      remoteTaskAbortsRef.current.get(`${cancellation.fromPeerId}:${cancellation.taskId}`)?.abort();
    };
    const onStatus = (event: Event) => {
      const detail = (event as CustomEvent<{ status: string; message: string }>).detail;
      if (detail.status === "disconnected" || detail.status === "error") setError(detail.message);
    };
    const onProgress = (event: Event) => {
      const progress = (event as CustomEvent<SwarmProgress>).detail;
      const stateLabel = {
        queued: "待機中",
        running: "生成中",
        complete: "完了",
        error: "エラー",
        cancelled: "中止",
      }[progress.state];
      setRemoteActivity(`${progress.deviceName}: ${stateLabel}`);
      if (["complete", "error", "cancelled"].includes(progress.state)) {
        window.setTimeout(() => setRemoteActivity(""), 1_500);
      }
    };
    swarmClient.addEventListener("peers", onPeers);
    swarmClient.addEventListener("latency", onLatency);
    swarmClient.addEventListener("task", onTask);
    swarmClient.addEventListener("cancel", onCancel);
    swarmClient.addEventListener("status", onStatus);
    swarmClient.addEventListener("progress", onProgress);
    return () => {
      swarmClient.removeEventListener("peers", onPeers);
      swarmClient.removeEventListener("latency", onLatency);
      swarmClient.removeEventListener("task", onTask);
      swarmClient.removeEventListener("cancel", onCancel);
      swarmClient.removeEventListener("status", onStatus);
      swarmClient.removeEventListener("progress", onProgress);
    };
  }, [gpu, inferenceMode, runGeneration, swarmClient]);

  const synthesizeResults = useCallback(
    async (
      basePayload: SwarmGenerationPayload,
      results: DistributedInferenceResult[],
      assistantId: string,
      signal?: AbortSignal,
    ) => {
      if (signal?.aborted) throw Object.assign(new Error("生成を停止しました。"), { name: "AbortError" });
      if (results.length === 1) {
        updateAssistant(assistantId, (message) => ({
          ...message,
          content: results[0].text,
          state: "complete",
          metrics: {
            source: "swarm",
            tokens: results[0].tokens,
            tokensPerSecond: results[0].tokensPerSecond,
          },
        }));
        return results[0].text;
      }
      const candidateText = results
        .map((result, index) => `候補${index + 1}（${result.role} / ${result.deviceName}）:\n${result.text.slice(0, 40_000)}`)
        .join("\n\n---\n\n");
      const synthesisPayload: SwarmGenerationPayload = {
        ...basePayload,
        messages: [
          ...basePayload.messages,
          {
            role: "user",
            content: `以下は複数端末が独立に作った回答候補です。正しい部分を統合し、矛盾や重複を除いて最終回答だけを返してください。\n\n${candidateText}`,
          },
        ],
      };
      const generated = await runGeneration(synthesisPayload, (output, tokensPerSecond) => {
        updateAssistant(assistantId, (message) => ({
          ...message,
          content: output,
          state: "streaming",
          metrics: { source: "swarm", tokensPerSecond },
        }));
      }, signal);
      updateAssistant(assistantId, (message) => ({
        ...message,
        content: generated.text,
        state: "complete",
        metrics: {
          source: "swarm",
          tokens: generated.tokens,
          tokensPerSecond: generated.tokensPerSecond,
        },
      }));
      return generated.text;
    },
    [runGeneration, updateAssistant],
  );

  const submit = useCallback(async () => {
    if (!account) {
      setError("ログインまたは登録してから利用してください。");
      return;
    }
    const text = input.trim();
    if ((!text && !attachments.length) || generating || !historyReady) return;
    const distributedEnabled = inferenceMode !== "local";
    const activeScope = inferenceMode === "world" ? "world" : "friend";
    const plannedPeers = distributedEnabled ? swarmClient.eligiblePeers(selectedModel.id, activeScope) : [];
    const nexusPlan = planNexusInference({
      inferenceMode,
      responseMode: settings.mode,
      availablePeerCount: plannedPeers.length,
      inputCharacters: text.length,
      attachmentCount: attachments.length,
    });
    if (plannedPeers.length) {
      const confirmed = window.confirm(
        `共有チャットを使います。\n\n現在の会話と添付内容を、接続中の参加者 ${plannedPeers.length} 件へ送って回答を作成します。続けますか？`,
      );
      if (!confirmed) return;
    }
    const controller = new AbortController();
    activeGenerationAbortRef.current = controller;
    setError("");
    setGenerating(true);
    setInput("");
    setAttachments([]);
    const userMessage: ChatMessage = {
      id: createId("message"),
      role: "user",
      content: text,
      attachments,
      createdAt: Date.now(),
      state: "complete",
    };
    const assistantId = createId("message");
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      createdAt: Date.now() + 1,
      state: "streaming",
    };
    const nextMessages = [...messages, userMessage];
    setMessages([...nextMessages, assistantMessage]);

    const profile = MODE_PROFILES[settings.mode];
    const payload: SwarmGenerationPayload = {
      messages: buildInferenceMessages(nextMessages, settings, selectedModel),
      mode: settings.mode,
      modelId: selectedModel.id,
      temperature: settings.temperature ?? profile.temperature,
      maxNewTokens: settings.maxNewTokens ?? profile.maxNewTokens,
      contextTokens: settings.contextTokens,
    };

    try {
      const preparedPayload = await preparePayload(payload, controller.signal);
      const eligible = distributedEnabled ? swarmClient.eligiblePeers(selectedModel.id, activeScope) : [];
      let completedBySwarm = false;
      let finalText = "";
      let resultSource: "local" | "swarm" = "local";
      let candidateCount = 0;
      if (eligible.length) {
        try {
          const results = await swarmClient.requestTasks(
            preparedPayload,
            Math.min(nexusPlan.targetPeerCount, eligible.length),
            settings.mode === "thinking" ? 180_000 : 120_000,
            controller.signal,
          );
          finalText = await synthesizeResults(preparedPayload, results, assistantId, controller.signal);
          updateAssistant(assistantId, (message) => ({
            ...message,
            metrics: message.metrics
              ? {
                  ...message.metrics,
                  nexusRoute: nexusPlan.routeLabel,
                  nexusTechId: nexusPlan.technologyId,
                }
              : {
                  source: "swarm",
                  nexusRoute: nexusPlan.routeLabel,
                  nexusTechId: nexusPlan.technologyId,
                },
          }));
          resultSource = "swarm";
          candidateCount = results.length;
          completedBySwarm = true;
        } catch (cause) {
          if (cause instanceof Error && cause.name === "AbortError") throw cause;
          setError(`共有チャットを完了できなかったため、通常のAIで続行します: ${errorText(cause)}`);
        }
      }
      if (!completedBySwarm) {
        const generated = await runGeneration(preparedPayload, (output, tokensPerSecond) => {
          updateAssistant(assistantId, (message) => ({
            ...message,
            content: output,
            state: "streaming",
            metrics: {
              source: "local",
              tokensPerSecond,
              nexusRoute: nexusPlan.routeLabel,
              nexusTechId: nexusPlan.technologyId,
            },
          }));
        }, controller.signal);
        updateAssistant(assistantId, (message) => ({
          ...message,
          content: generated.text,
          state: "complete",
          metrics: {
            source: "local",
            tokens: generated.tokens,
            tokensPerSecond: generated.tokensPerSecond,
            nexusRoute: nexusPlan.routeLabel,
            nexusTechId: nexusPlan.technologyId,
            ragSources: generated.rag?.sources,
          },
        }));
        finalText = generated.text;
      }
      swarmClient.appendLedger("chat-turn", assistantId, {
        v: 1,
        user: {
          id: userMessage.id,
          content: truncateUtf8(userMessage.content, 72_000),
          createdAt: userMessage.createdAt,
          attachments: (userMessage.attachments ?? []).map((attachment) => ({
            name: attachment.name.slice(0, 255),
            size: attachment.size,
            mimeType: attachment.mimeType.slice(0, 127),
            truncated: attachment.truncated,
          })),
        },
        assistant: {
          id: assistantId,
          content: truncateUtf8(finalText, 112_000),
          createdAt: assistantMessage.createdAt,
          source: resultSource,
        },
        modelId: selectedModel.id,
        mode: settings.mode,
        candidates: candidateCount,
        nexus: {
          technologyId: nexusPlan.technologyId,
          route: nexusPlan.route,
          routeLabel: nexusPlan.routeLabel,
          targetPeerCount: nexusPlan.targetPeerCount,
          availablePeerCount: nexusPlan.availablePeerCount,
          payloadPath: nexusPlan.payloadPath,
          featureMatrix: nexusPlan.featureMatrix,
          layerStream: nexusPlan.layerStream,
          keepsModelOffUserDevice: nexusPlan.keepsModelOffUserDevice,
        },
      });
    } catch (cause) {
      const message = errorText(cause);
      const aborted = cause instanceof Error && cause.name === "AbortError";
      if (!aborted) setError(message);
      updateAssistant(assistantId, (current) => ({
        ...current,
        content: current.content || (aborted ? "生成を停止しました。" : `エラー: ${message}`),
        state: aborted ? "complete" : "error",
      }));
    } finally {
      if (activeGenerationAbortRef.current === controller) activeGenerationAbortRef.current = null;
      setGenerating(false);
    }
  }, [
    attachments,
    generating,
    account,
    historyReady,
    inferenceMode,
    input,
    messages,
    preparePayload,
    runGeneration,
    selectedModel,
    settings,
    swarmClient,
    synthesizeResults,
    updateAssistant,
  ]);

  const addFiles = useCallback(async (files: FileList | File[]) => {
    if (!account) {
      setError("ログインまたは登録してからファイルを追加してください。");
      return;
    }
    setError("");
    try {
      const available = Math.max(0, 5 - attachments.length);
      const selected = Array.from(files).slice(0, available);
      const read = await Promise.all(selected.map(readAttachment));
      setAttachments((current) => [...current, ...read]);
    } catch (cause) {
      setError(errorText(cause));
    }
  }, [account, attachments.length]);

  const onFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) void addFiles(event.target.files);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length) void addFiles(event.dataTransfer.files);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submit();
    }
  };

  const selectModel = (model: ModelDefinition) => {
    if (model.id === selectedModel.id) return;
    setSelectedModelId(model.id);
    saveModelId(model.id);
    setLoadState(loadedModelIdRef.current === model.id
      ? { status: "ready", modelId: model.id }
      : { status: "idle" });
    if (swarm && gpu) swarmClient.updatePresence(model.id, gpu, false, inferenceMode === "world" ? "world" : "friend");
  };

  const startFastModel = () => {
    if (!account) {
      setError("ログインまたは登録してからモデルを読み込んでください。");
      return;
    }
    const fastModel = getModel("qwen3-0.6b-fast");
    selectModel(fastModel);
    void ensureLoaded(fastModel).catch((cause) => setError(errorText(cause)));
  };

  const syncRoomHistory = async () => {
    try {
      const remoteMessages = await swarmClient.readChatHistory();
      if (!remoteMessages.length) return;
      setMessages((current) => {
        const merged = new Map(remoteMessages.map((message) => [message.id, message]));
        for (const message of current) merged.set(message.id, message);
        return [...merged.values()]
          .sort((left, right) => left.createdAt - right.createdAt)
          .slice(-500);
      });
    } catch (cause) {
      setError(`共有チャットには接続しましたが、履歴を同期できませんでした: ${errorText(cause)}`);
    }
  };

  const createSwarm = async (name: string) => {
    if (!account) throw new Error("ログインまたは登録してから共有チャットを利用してください。");
    if (!gpu) throw new Error("AIの確認が終わっていません。");
    const session = await swarmClient.create({
      deviceName: name,
      modelId: selectedModel.id,
      gpu,
      acceptTasks: inferenceMode !== "local" && contributing && !generating && loadedModelIdRef.current === selectedModel.id,
      scope: inferenceMode === "world" ? "world" : "friend",
    });
    setSwarm(session);
    setTurnConfigured(swarmClient.hasTurn);
    setInferenceMode((current) => current === "local" ? "friend" : current);
    await syncRoomHistory();
  };

  const joinSwarm = async (code: string, passcode: string, name: string) => {
    if (!account) throw new Error("ログインまたは登録してから共有チャットを利用してください。");
    if (!gpu) throw new Error("AIの確認が終わっていません。");
    const session = await swarmClient.join({
      code,
      passcode,
      deviceName: name,
      modelId: selectedModel.id,
      gpu,
      acceptTasks: inferenceMode !== "local" && contributing && !generating && loadedModelIdRef.current === selectedModel.id,
      scope: inferenceMode === "world" ? "world" : "friend",
    });
    setSwarm(session);
    setTurnConfigured(swarmClient.hasTurn);
    setInferenceMode((current) => current === "local" ? "friend" : current);
    await syncRoomHistory();
  };

  const leaveSwarm = async () => {
    for (const controller of remoteTaskAbortsRef.current.values()) controller.abort();
    remoteTaskAbortsRef.current.clear();
    await swarmClient.leave();
    setSwarm(null);
    setPeers([]);
    setInferenceMode("local");
    setSignalingRtt(null);
  };

  const saveAccount = async (displayName: string) => {
    const response = await fetch("/api/account", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ displayName }),
    });
    const body = await response.json().catch(() => ({})) as {
      account?: AccountProfile;
      message?: string;
      error?: string;
    };
    if (!response.ok || !body.account) {
      throw new Error(body.message || body.error || "アカウントを保存できませんでした。");
    }
    setAccount(body.account);
    if (!deviceName.trim()) setDeviceName(body.account.displayName.slice(0, 32));
  };

  const registerAccount = async (input: { loginId: string; displayName: string; password: string }) => {
    const profile = await accountRequest("/api/account/register", input, "登録できませんでした。");
    setAccount(profile);
    setAccountChecked(true);
    if (!deviceName.trim()) setDeviceName(profile.displayName.slice(0, 32));
    return profile;
  };

  const loginAccount = async (input: { loginId: string; password: string }) => {
    const profile = await accountRequest("/api/account/login", input, "ログインできませんでした。");
    setAccount(profile);
    setAccountChecked(true);
    if (!deviceName.trim()) setDeviceName(profile.displayName.slice(0, 32));
    return profile;
  };

  const deleteAccount = async () => {
    const response = await fetch("/api/account", {
      method: "DELETE",
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("アカウントを削除できませんでした。");
    setAccount(null);
    setAccountChecked(true);
  };

  const clearHistory = async () => {
    if (!window.confirm("この会話履歴を消去します。元に戻せません。")) return;
    await clearMessagesFromDb();
    setMessages([]);
  };

  const stopGeneration = useCallback(() => {
    activeGenerationAbortRef.current?.abort();
    swarmClient.cancelPendingBatches();
    inference.interrupt();
  }, [inference, swarmClient]);

  const newChat = () => {
    if (generating) stopGeneration();
    setMessages([]);
    setError("");
    void clearMessagesFromDb();
  };

  const loadDescription = statusText(loadState, gpu, selectedModel);
  const onlinePeerCount = peers.filter((peer) => peer.dataChannelState === "open").length;

  if (!booting && !accountChecked) {
    return <AuthGate loading={true} onRegister={registerAccount} onLogin={loginAccount} />;
  }

  if (!booting && accountChecked && account === null) {
    return <AuthGate loading={!accountChecked} onRegister={registerAccount} onLogin={loginAccount} />;
  }

  return (
    <>
      {booting && <BootSplash onComplete={() => setBooting(false)} />}
    <div
      className={`app-shell${dragging ? " is-dragging" : ""}`}
      aria-hidden={booting || undefined}
      inert={booting || undefined}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => event.currentTarget === event.target && setDragging(false)}
      onDrop={onDrop}
    >
      <aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
        <div className="brand">
          <div className="brand-mark"><Boxes size={21} /></div>
          <div><strong>Nexus</strong><span>AI Chat</span></div>
          <button type="button" className="icon-button sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="メニューを閉じる"><X size={18} /></button>
        </div>
        <button type="button" className="new-chat-button" onClick={newChat}><MessageSquarePlus size={18} /> 新しいチャット</button>
        <div className="sidebar-section">
          <span className="sidebar-label">チャット</span>
          <button type="button" className="conversation-item active">
            <Bot size={16} />
            <div><strong>{messages.find((message) => message.role === "user")?.content.slice(0, 24) || "新しいチャット"}</strong><span>会話履歴</span></div>
          </button>
        </div>
        <div className="sidebar-bottom">
          <div className="sidebar-quick-actions" aria-label="主要操作">
            <button type="button" onClick={() => setSettingsOpen(true)}><Settings size={15} /> 設定</button>
            <button type="button" onClick={() => setModelsOpen(true)}><Cpu size={15} /> AI</button>
            <button type="button" onClick={newChat}><MessageSquarePlus size={15} /> 新規</button>
          </div>
          <div className="privacy-badge"><ShieldCheck size={16} /><div><strong>安全に利用</strong><span>会話はログイン中の環境で扱われます</span></div></div>
        </div>
      </aside>
      {sidebarOpen && <button type="button" className="mobile-overlay" onClick={() => setSidebarOpen(false)} aria-label="メニューを閉じる" />}

      <main className="main-panel">
        <header className="topbar">
          <div className="topbar-left">
            <button type="button" className="icon-button menu-button" onClick={() => setSidebarOpen(true)} aria-label="メニューを開く"><Menu size={20} /></button>
            <button type="button" className="model-selector" onClick={() => setModelsOpen(true)}>
              <span className="model-dot" />
              <span><small>AI</small><strong>{selectedModel.name}</strong></span>
              <ChevronDown size={15} />
            </button>
          </div>
          <div className="topbar-actions">
            <label className="mode-select">
              {settings.mode === "fast" ? <Zap size={15} /> : <Sparkles size={15} />}
              <select
                aria-label="応答モード"
                value={settings.mode}
                onChange={(event) => {
                  const mode = event.target.value as AppSettings["mode"];
                  setSettings({
                    ...settings,
                    mode,
                    temperature: MODE_PROFILES[mode].temperature,
                    maxNewTokens: MODE_PROFILES[mode].maxNewTokens,
                  });
                }}
              >
                <option value="thinking">Thinking</option>
                <option value="fast">Fast</option>
                <option value="medium">Medium</option>
              </select>
            </label>
            {swarm && (
              <button type="button" className={`swarm-chip${inferenceMode !== "local" ? " active" : ""}`} onClick={() => setSettingsOpen(true)}>
                <Network size={15} /> 共有中
              </button>
            )}
            <button type="button" className="settings-button" onClick={() => setSettingsOpen(true)} aria-label="設定"><Settings size={17} /><span>設定</span></button>
          </div>
        </header>

        <div className="runtime-strip" aria-live="polite">
          <span className={`runtime-dot ${gpu?.available ? "online" : gpu ? "error" : "checking"}`} />
          <span>{loadDescription}</span>
          {swarm && <><i /> <span>共有チャット接続中</span></>}
          {remoteActivity && <><i /> <span className="remote-activity">{remoteActivity}</span></>}
        </div>

        <section className="chat-area">
          <div className="messages-scroll">
            <div className="messages-container">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-orbit"><span /><div><Sparkles size={30} /></div></div>
                  <p className="eyebrow">AI ASSISTANT</p>
                  <h1>AIと話す。</h1>
                  <p className="empty-copy">
                    質問、相談、文章作成、ファイルの確認まで、いつものチャット感覚で使えます。
                  </p>
                  <div className="empty-actions">
                    <button type="button" className="button button--primary button--large" onClick={startFastModel}><Zap size={17} /> すぐ始める</button>
                    <button type="button" className="button button--secondary button--large" onClick={() => setModelsOpen(true)}><Cpu size={17} /> AIを選ぶ</button>
                    <button type="button" className="button button--secondary button--large" onClick={() => { setSettingsOpen(true); }}><Settings size={17} /> 設定</button>
                  </div>
                  <div className="feature-line">
                    <span><ShieldCheck size={15} /> 安全に利用</span>
                    <span><Sparkles size={15} /> 自然な会話</span>
                    <span><FileText size={15} /> ファイル対応</span>
                  </div>
                </div>
              ) : (
                messages.map((message) => <MessageBubble key={message.id} message={message} />)
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="composer-zone">
            {error && <div className="global-error" role="alert"><span>{error}</span><button type="button" onClick={() => setError("")} aria-label="エラーを閉じる"><X size={15} /></button></div>}
            <div className={`composer${dragging ? " composer--drop" : ""}`}>
              {attachments.length > 0 && (
                <div className="attachment-list">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="file-pill">
                      <FileText size={15} />
                      <span><strong>{attachment.name}</strong><small>{formatBytes(attachment.size)}{attachment.truncated ? " · 一部のみ" : ""}</small></span>
                      <button type="button" onClick={() => setAttachments((current) => current.filter((item) => item.id !== attachment.id))} aria-label={`${attachment.name}を削除`}><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={onInputKeyDown}
                rows={1}
                maxLength={80_000}
                placeholder={historyReady ? "メッセージを入力…" : "会話履歴を読み込んでいます…"}
                disabled={!historyReady}
                aria-label="メッセージ"
              />
              <div className="composer-actions">
                <div className="composer-left">
                  <input
                    ref={fileInputRef}
                    className="sr-only"
                    type="file"
                    multiple
                    accept="text/*,.md,.markdown,.csv,.tsv,.json,.yaml,.yml,.xml,.js,.jsx,.ts,.tsx,.py,.java,.c,.cpp,.h,.hpp,.rs,.go,.rb,.php,.sql,.log"
                    onChange={onFileInput}
                  />
                  <button type="button" className="attach-button" onClick={() => fileInputRef.current?.click()} disabled={attachments.length >= 5} aria-label="ファイルを追加"><Paperclip size={17} /> <span>ファイル</span></button>
                  <span className="context-meter">{settings.contextTokens.toLocaleString()} context</span>
                  {inferenceMode !== "local" && onlinePeerCount > 0 && <span className="p2p-meter"><Network size={13} /> 共有中</span>}
                </div>
                {generating ? (
                  <button type="button" className="send-button stop" onClick={stopGeneration} aria-label="生成を停止"><CircleStop size={19} /></button>
                ) : (
                  <button type="button" className="send-button" onClick={() => void submit()} disabled={!historyReady || (!input.trim() && !attachments.length)} aria-label="送信"><ArrowUp size={19} /></button>
                )}
              </div>
            </div>
            <p className="composer-note">AIの回答は確認して利用してください</p>
          </div>
        </section>
      </main>

      <div className="floating-actions" aria-label="固定操作">
        <button type="button" onClick={() => setSettingsOpen(true)} aria-label="設定を開く"><Settings size={17} /><span>設定</span></button>
      </div>

      {dragging && <div className="drop-overlay"><Paperclip size={30} /><strong>ここにファイルをドロップ</strong><span>最大5件・1件2 MBまで</span></div>}

      <ModelLibrary
        open={modelsOpen}
        selectedModel={selectedModel}
        loadState={loadState}
        gpu={gpu}
        onClose={() => setModelsOpen(false)}
        onSelect={selectModel}
        onLoad={(model) => void ensureLoaded(model).catch((cause) => setError(errorText(cause)))}
      />
      <SettingsDrawer
        open={settingsOpen}
        settings={settings}
        model={selectedModel}
        loadState={loadState}
        gpu={gpu}
        swarm={swarm}
        peers={peers}
        signalingRttMs={signalingRtt}
        turnConfigured={turnConfigured}
        inferenceMode={inferenceMode}
        contributing={contributing}
        account={account}
        deviceName={deviceName}
        onClose={() => setSettingsOpen(false)}
        onSettings={setSettings}
        onOpenModels={() => { setSettingsOpen(false); setModelsOpen(true); }}
        onLoadModel={() => void ensureLoaded(selectedModel).catch((cause) => setError(errorText(cause)))}
        onCreateSwarm={createSwarm}
        onJoinSwarm={joinSwarm}
        onLeaveSwarm={leaveSwarm}
        onInferenceMode={setInferenceMode}
        onContributing={setContributing}
        onSaveAccount={saveAccount}
        onDeleteAccount={deleteAccount}
        onDeviceName={setDeviceName}
        onClearData={clearHistory}
      />
    </div>
    </>
  );
}

async function fetchAccount(): Promise<AccountProfile | null> {
  const response = await fetch("/api/account", {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!response.ok) return null;
  const body = await response.json().catch(() => ({})) as { account?: AccountProfile | null };
  return body.account ?? null;
}

async function accountRequest(path: string, input: Record<string, string>, fallback: string): Promise<AccountProfile> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(input),
  });
  const body = await response.json().catch(() => ({})) as {
    account?: AccountProfile;
    message?: string;
    error?: string;
  };
  if (!response.ok || !body.account) {
    throw new Error(body.message || body.error || fallback);
  }
  return body.account;
}
